import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

const f = (v) => (v || v === 0 ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—');
const COLS = [
  ['gross', 'Gross sales'], ['sales_vat', 'Sales VAT'], ['expected_commission', 'Exp. commission'], ['expected_commission_vat', 'Exp. VAT on comm.'],
  ['actual_commission', 'Act. commission'], ['actual_commission_vat', 'Act. VAT on comm.'], ['excess_commission', 'Excess comm.'],
  ['excess_commission_vat', 'Excess VAT'], ['itemized', 'Refunds / penalties / adj.'], ['unexplained', 'Unexplained'],
  ['expected_net', 'Exp. net'], ['actual_settlement', 'Act. settlement'], ['bank_receipt', 'Booked to bank'], ['difference', 'Difference'],
];
const itemized = (r) => (r.actual_refunds || 0) + (r.actual_penalties || 0) + (r.actual_adjustments || 0) + (r.actual_other || 0);

export function SettlementReconTable({ branchId, startDate, endDate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams();
    if (startDate) p.append('start_date', startDate);
    if (endDate) p.append('end_date', endDate);
    if (branchId && branchId !== 'all') p.append('branch_id', branchId);
    api.get(`/platforms/settlement-reconciliation?${p}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], unsettled: [], totals: {} }));
  }, [branchId, startDate, endDate]);
  if (!data) return null;
  return (
    <Card data-testid="settlement-recon">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Settlement reconciliation — by platform, period and branch (exact match)</CardTitle>
        <p className="text-xs text-muted-foreground">Expected = agreement in force at each sale date. {data.provisional_note}</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="settlement-recon-table">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-1 pr-2">Platform / period / branch</th>
              {COLS.map(([k, l]) => <th key={k} className="py-1 px-1 text-right whitespace-nowrap">{l}</th>)}
              <th className="py-1 pl-2">Status / reason</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => <Row key={i} r={r} i={i} />)}
            {data.rows.length === 0 && <tr><td colSpan={COLS.length + 2} className="py-6 text-center text-muted-foreground">No recorded settlements in this period</td></tr>}
          </tbody>
        </table>
        {data.unsettled.length > 0 && (
          <div className="mt-3 text-xs" data-testid="settlement-recon-unsettled">
            <div className="font-semibold mb-1">Sales not yet in any settlement (outstanding from platform)</div>
            {data.unsettled.map((u, i) => (
              <div key={i} className="flex gap-3"><span className="w-56 truncate">{u.platform} · {u.branch}</span><span>{u.count} sales</span>
                <span>gross {cur()} {f(u.gross)}</span><span>expected net {cur()} {f(u.expected_net)}</span></div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ r, i }) {
  const ok = r.status === 'reconciled';
  return (
    <tr className="border-b align-top" data-testid={`settlement-recon-row-${i}`}>
      <td className="py-1 pr-2 whitespace-nowrap">
        <div className="font-medium">{r.platform}</div>
        <div className="text-muted-foreground">{r.period} · {r.branch}</div>
        <div className="text-muted-foreground">agreed {r.agreed?.commission_rate ?? 0}% + VAT {r.agreed?.commission_vat_rate ?? '15?'}% {r.provisional && <Badge className="ml-1 bg-warning/20 text-warning">basis unverified</Badge>}</div>
      </td>
      {COLS.map(([k]) => {
        const v = k === 'itemized' ? itemized(r) : r[k];
        const bad = ['excess_commission', 'excess_commission_vat', 'unexplained', 'difference'].includes(k) && v;
        return <td key={k} className={`py-1 px-1 text-right tabular-nums ${bad ? 'text-error font-semibold' : ''}`}>{f(v)}</td>;
      })}
      <td className="py-1 pl-2 min-w-[200px]">
        <Badge className={ok ? 'bg-success/20 text-success' : 'bg-error/20 text-error'} data-testid={`settlement-recon-status-${i}`}>{r.rounding_only ? 'rounding difference' : r.status}</Badge>
        <div className="text-muted-foreground mt-0.5">{r.reason}</div>
      </td>
    </tr>
  );
}
